import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import DataTable from '../../components/common/DataTable';
import StatusBadge from '../../components/common/StatusBadge';
import { LEAD_STATUS_MAP } from '../../utils/constants';
import Modal from '../../components/common/Modal';
import { useAuth } from '../../contexts/AuthContext';

const LEAD_SOURCES = [
  'walk-in',
  'referral',
  'field',
  'phone',
  'social-media',
  'campaign',
  'other',
];

const LEAD_STATUSES = Object.keys(LEAD_STATUS_MAP);

const isCollectionRole = (roleName) => {
  if (!roleName) return false;
  return roleName.toLowerCase().includes('collection');
};

// ═══════════════════════════════════════════════════════════════════
// PRE-COMPUTED STYLES
// ═══════════════════════════════════════════════════════════════════

const F = {
  section: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: 10,
    padding: '16px 20px',
    marginBottom: 16,
    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
  },

  row: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 12,
    alignItems: 'flex-end',
  },

  group: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    minWidth: 0,
  },

  label: {
    fontSize: 11,
    fontWeight: 600,
    color: '#64748b',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    lineHeight: 1,
    display: 'flex',
    alignItems: 'center',
    gap: 4,
  },

  select: {
    padding: '8px 28px 8px 10px',
    borderRadius: 6,
    border: '1px solid #d1d5db',
    fontSize: 13,
    background: '#fff',
    outline: 'none',
    color: '#1e293b',
    minWidth: 155,
    cursor: 'pointer',
    appearance: 'none',
    WebkitAppearance: 'none',
    backgroundImage:
      'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'12\' height=\'12\' viewBox=\'0 0 12 12\'%3E%3Cpath fill=\'%236b7280\' d=\'M6 8L1 3h10z\'/%3E%3C/svg%3E")',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 8px center',
  },

  input: {
    padding: '8px 10px 8px 32px',
    borderRadius: 6,
    border: '1px solid #d1d5db',
    fontSize: 13,
    background: '#fff',
    outline: 'none',
    color: '#1e293b',
    minWidth: 200,
  },

  date: {
    padding: '8px 10px',
    borderRadius: 6,
    border: '1px solid #d1d5db',
    fontSize: 13,
    background: '#fff',
    outline: 'none',
    color: '#1e293b',
    width: 155,
  },

  searchWrap: {
    position: 'relative',
  },

  searchIcon: {
    position: 'absolute',
    left: 10,
    top: '50%',
    transform: 'translateY(-50%)',
    color: '#94a3b8',
    fontSize: 13,
    pointerEvents: 'none',
  },

  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 18,
    height: 18,
    borderRadius: 9,
    background: '#2563eb',
    color: '#fff',
    fontSize: 10,
    fontWeight: 700,
  },

  btnFilter: {
    padding: '8px 22px',
    borderRadius: 8,
    border: 'none',
    background: '#2563eb',
    color: '#fff',
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: 600,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    whiteSpace: 'nowrap',
  },

  btnClear: {
    padding: '8px 18px',
    borderRadius: 8,
    border: '1px solid #d1d5db',
    background: '#fff',
    color: '#374151',
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: 500,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    whiteSpace: 'nowrap',
  },

  btnExport: {
    padding: '8px 18px',
    borderRadius: 8,
    border: '1px solid #059669',
    background: '#ecfdf5',
    color: '#059669',
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: 600,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    whiteSpace: 'nowrap',
  },

  btnNew: {
    padding: '8px 18px',
    borderRadius: 8,
    border: 'none',
    background: '#059669',
    color: '#fff',
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: 600,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    whiteSpace: 'nowrap',
    textDecoration: 'none',
  },

  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 8,
    alignItems: 'flex-end',
    marginLeft: 'auto',
  },

  activeRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 6,
    alignItems: 'center',
    marginTop: 10,
    paddingTop: 10,
    borderTop: '1px solid #f1f5f9',
  },

  tag: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    padding: '3px 10px',
    borderRadius: 6,
    background: '#eff6ff',
    color: '#1e40af',
    fontSize: 12,
    fontWeight: 500,
    border: '1px solid #bfdbfe',
  },

  tagX: {
    background: 'none',
    border: 'none',
    color: '#64748b',
    cursor: 'pointer',
    fontSize: 12,
    padding: '0 2px',
    lineHeight: 1,
  },

  clearAll: {
    background: 'none',
    border: 'none',
    color: '#64748b',
    cursor: 'pointer',
    fontSize: 11,
    textDecoration: 'underline',
    padding: '2px 4px',
  },

  dateRow: {
    display: 'flex',
    gap: 0,
    alignItems: 'flex-end',
  },

  dateSep: {
    padding: '8px 6px',
    color: '#94a3b8',
    fontSize: 13,
    lineHeight: 1,
  },
};

export default function LeadList() {
  const { user } = useAuth();

  const perms = {
    isCO: isCollectionRole(user?.role?.name),

    canReassign: !isCollectionRole(user?.role?.name),

    canDelete: !isCollectionRole(user?.role?.name),

    canCreate: !isCollectionRole(user?.role?.name),
  };

  // ═══════════════════════════════════════════════════════════════════
  // STATE
  // ═══════════════════════════════════════════════════════════════════

  const [leads, setLeads] = useState([]);

  const [page, setPage] = useState(1);

  const [pagination, setPagination] = useState(null);

  const [isExporting, setIsExporting] = useState(false);

  const [stats, setStats] = useState(null);

  const [filters, setFilters] = useState({
    search: '',
    status: '',
    source: '',
    assignedTo: '',
    startDate: '',
    endDate: '',
  });

  const [searchRaw, setSearchRaw] = useState('');

  const searchTimeout = useRef(null);

  const [debouncedSearch, setDebouncedSearch] = useState('');

  // ═══════════════════════════════════════════════════════════════════
  // EDIT MODAL
  // ═══════════════════════════════════════════════════════════════════

  const [showEditModal, setShowEditModal] = useState(false);

  const [editingLead, setEditingLead] = useState(null);

  const [editForm, setEditForm] = useState({});

  const [editError, setEditError] = useState('');

  const [editSaving, setEditSaving] = useState(false);

  // ═══════════════════════════════════════════════════════════════════
  // REASSIGN MODAL
  // ═══════════════════════════════════════════════════════════════════

  const [showReassignModal, setShowReassignModal] = useState(false);

  const [reassigningLead, setReassigningLead] = useState(null);

  const [selectedLO, setSelectedLO] = useState('');

  const [reassignSaving, setReassignSaving] = useState(false);

  const [reassignError, setReassignError] = useState('');

  const [loLoading, setLoLoading] = useState(false);

  const [loanOfficers, setLoanOfficers] = useState([]);

  // ═══════════════════════════════════════════════════════════════════
  // FILTER HELPERS
  // ═══════════════════════════════════════════════════════════════════

  const updateFilter = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));

    setPage(1);
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;

    setSearchRaw(value);

    clearTimeout(searchTimeout.current);

    searchTimeout.current = setTimeout(() => {
      setDebouncedSearch(value);

      updateFilter('search', value);
    }, 400);
  };

  const clearFilter = (key) => {
    if (key === 'search') {
      setSearchRaw('');
      setDebouncedSearch('');
    }

    const defaults = {
      search: '',
      status: '',
      source: '',
      assignedTo: '',
      startDate: '',
      endDate: '',
    };

    setFilters((prev) => ({
      ...prev,
      [key]: defaults[key],
    }));

    setPage(1);
  };

  const clearAllFilters = () => {
    setSearchRaw('');

    setDebouncedSearch('');

    setFilters({
      search: '',
      status: '',
      source: '',
      assignedTo: '',
      startDate: '',
      endDate: '',
    });

    setPage(1);
  };

  const activeFilterCount = [
    filters.search,
    filters.status,
    filters.source,
    filters.assignedTo,
    filters.startDate,
    filters.endDate,
  ].filter(Boolean).length;

  const hasActiveFilters = activeFilterCount > 0;

  // ═══════════════════════════════════════════════════════════════════
  // FETCH LEADS
  // ═══════════════════════════════════════════════════════════════════

  const fetchLeads = useCallback(async () => {
    try {
      const params = new URLSearchParams({
        page: String(page),
        limit: '20',
        search: debouncedSearch || '',
      });

      if (filters.status) {
        params.append('status', filters.status);
      }

      if (filters.source) {
        params.append('source', filters.source);
      }

      if (filters.assignedTo) {
        params.append('assignedTo', filters.assignedTo);
      }

      if (filters.startDate) {
        params.append('startDate', filters.startDate);
      }

      if (filters.endDate) {
        params.append('endDate', filters.endDate);
      }

      const res = await api.get(`/leads?${params.toString()}`);

      setLeads(res.data?.data?.leads || []);

      setPagination(res.data?.data?.pagination || null);
    } catch (err) {
      console.error('Failed to fetch leads:', err);
    }
  }, [
    page,
    debouncedSearch,
    filters.status,
    filters.source,
    filters.assignedTo,
    filters.startDate,
    filters.endDate,
  ]);

  // ═══════════════════════════════════════════════════════════════════
  // FETCH STATS
  // ═══════════════════════════════════════════════════════════════════

  const fetchStats = useCallback(async () => {
    try {
      const res = await api.get('/leads/stats');

      setStats(res.data?.data || null);
    } catch {
      // Statistics are optional.
    }
  }, []);

  // ═══════════════════════════════════════════════════════════════════
  // FETCH LOAN OFFICERS
  // ═══════════════════════════════════════════════════════════════════

  const fetchLoanOfficers = useCallback(async () => {
    setLoLoading(true);

    try {
      const res = await api.get(
        '/users?limit=200&isActive=true'
      );

      const users =
        res.data?.data?.users ||
        res.data?.data ||
        [];

      const loRoles = [
        'loan-officer',
        'loan_officer',
        'lo',
        'field-officer',
        'field_officer',
      ];

      const officers = users.filter((u) => {
        const roleName =
          u.role?.name?.toLowerCase() ||
          u.roleName?.toLowerCase() ||
          '';

        return loRoles.some((role) =>
          roleName.includes(role)
        );
      });

      setLoanOfficers(
        officers.length === 0 && users.length > 0
          ? users
          : officers
      );
    } catch {
      try {
        const res = await api.get(
          '/users/loan-officers'
        );

        setLoanOfficers(
          res.data?.data || []
        );
      } catch {
        setLoanOfficers([]);
      }
    } finally {
      setLoLoading(false);
    }
  }, []);

  // ═══════════════════════════════════════════════════════════════════
  // EFFECTS
  // ═══════════════════════════════════════════════════════════════════

  useEffect(() => {
    fetchLeads();
  }, [fetchLeads]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  useEffect(() => {
    if (!perms.isCO) {
      fetchLoanOfficers();
    }
  }, [perms.isCO, fetchLoanOfficers]);

  useEffect(() => {
    return () => {
      clearTimeout(searchTimeout.current);
    };
  }, []);

  const visibleLeads = leads.filter(
    (lead) => lead.status !== 'converted'
  );

  // ═══════════════════════════════════════════════════════════════════
  // DELETE
  // ═══════════════════════════════════════════════════════════════════

  const handleDelete = async (id) => {
    if (
      !window.confirm(
        'Are you sure you want to delete this lead? This action cannot be undone.'
      )
    ) {
      return;
    }

    try {
      await api.delete(`/leads/${id}`);

      await fetchLeads();

      await fetchStats();
    } catch (err) {
      alert(
        err.response?.data?.message ||
          'Failed to delete lead.'
      );
    }
  };

  // ═══════════════════════════════════════════════════════════════════
  // EDIT
  // ═══════════════════════════════════════════════════════════════════

  const openEditModal = (lead) => {
    setEditingLead(lead);

    setEditForm({
      firstName: lead.firstName || '',
      lastName: lead.lastName || '',
      phone: lead.phone || '',
      email: lead.email || '',
      nationalId: '',
      address: lead.address || '',
      source: lead.source || 'walk-in',
      followUpDate: lead.followUpDate
        ? lead.followUpDate.split('T')[0]
        : '',
      notes: lead.notes || '',
    });

    setEditError('');

    setShowEditModal(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();

    setEditSaving(true);

    setEditError('');

    try {
      const payload = {
        ...editForm,
      };

      Object.keys(payload).forEach((key) => {
        if (
          payload[key] === '' &&
          key !== 'firstName' &&
          key !== 'lastName' &&
          key !== 'phone'
        ) {
          delete payload[key];
        }
      });

      if (!payload.nationalId) {
        delete payload.nationalId;
      }

      await api.put(
        `/leads/${editingLead._id}`,
        payload
      );

      setShowEditModal(false);

      await fetchLeads();

      await fetchStats();
    } catch (err) {
      const errors =
        err.response?.data?.errors;

      if (Array.isArray(errors)) {
        setEditError(
          errors
            .map((error) => error.message)
            .join(', ')
        );
      } else {
        setEditError(
          err.response?.data?.message ||
            'Failed to update lead.'
        );
      }
    } finally {
      setEditSaving(false);
    }
  };

  // ═══════════════════════════════════════════════════════════════════
  // REASSIGN
  // ═══════════════════════════════════════════════════════════════════

  const openReassignModal = async (lead) => {
    if (!perms.canReassign) {
      return;
    }

    setReassigningLead(lead);

    setSelectedLO(
      lead.assignedTo?._id || ''
    );

    setReassignError('');

    setShowReassignModal(true);

    if (loanOfficers.length === 0) {
      await fetchLoanOfficers();
    }
  };

  const handleReassignSubmit = async (e) => {
    e.preventDefault();

    if (!selectedLO) {
      setReassignError(
        'Please select a Loan Officer'
      );

      return;
    }

    if (
      reassigningLead.assignedTo?._id ===
      selectedLO
    ) {
      setReassignError(
        'Already assigned to this officer'
      );

      return;
    }

    setReassignSaving(true);

    setReassignError('');

    try {
      await api.post(
        `/leads/${reassigningLead._id}/assign`,
        {
          assignedTo: selectedLO,
        }
      );

      setShowReassignModal(false);

      setReassigningLead(null);

      setSelectedLO('');

      await fetchLeads();
    } catch (err) {
      const errors =
        err.response?.data?.errors;

      if (Array.isArray(errors)) {
        setReassignError(
          errors
            .map((error) => error.message)
            .join(', ')
        );
      } else {
        setReassignError(
          err.response?.data?.message ||
            'Failed to reassign.'
        );
      }
    } finally {
      setReassignSaving(false);
    }
  };

  const closeReassignModal = () => {
    setShowReassignModal(false);

    setReassigningLead(null);

    setSelectedLO('');

    setReassignError('');
  };

  // ═══════════════════════════════════════════════════════════════════
  // EXPORT ALL MATCHING LEADS
  // ═══════════════════════════════════════════════════════════════════

  const handleExport = async () => {
    setIsExporting(true);

    try {
      const pageSize = 1000;

      let pageNo = 1;

      let total = 0;

      let all = [];

      do {
        const params = new URLSearchParams({
          page: String(pageNo),
          limit: String(pageSize),
        });

        if (filters.search) {
          params.append(
            'search',
            filters.search
          );
        }

        if (filters.status) {
          params.append(
            'status',
            filters.status
          );
        }

        if (filters.source) {
          params.append(
            'source',
            filters.source
          );
        }

        if (filters.assignedTo) {
          params.append(
            'assignedTo',
            filters.assignedTo
          );
        }

        if (filters.startDate) {
          params.append(
            'startDate',
            filters.startDate
          );
        }

        if (filters.endDate) {
          params.append(
            'endDate',
            filters.endDate
          );
        }

        const res = await api.get(
          `/leads?${params.toString()}`
        );

        const result =
          res.data?.data || {};

        const rows = Array.isArray(
          result.leads
        )
          ? result.leads
          : [];

        all = all.concat(rows);

        total = Number(
          result.pagination?.total ??
            result.pagination?.totalRecords ??
            all.length
        );

        pageNo += 1;

        if (rows.length < pageSize) {
          break;
        }

        if (pageNo > 1000) {
          break;
        }
      } while (all.length < total);

      if (!all.length) {
        alert(
          'No leads match the selected filters.'
        );

        return;
      }

      /*
       * Flatten all values before sending them
       * to the Excel generator.
       *
       * React render functions must NEVER be
       * sent as export values because JSON
       * serialization removes functions.
       */

      const exportRows = all.map((lead) => {
        const leadName = [
          lead.firstName,
          lead.lastName,
        ]
          .filter(Boolean)
          .join(' ')
          .trim();

        const assignedOfficer =
          lead.assignedTo
            ? [
                lead.assignedTo.firstName,
                lead.assignedTo.lastName,
              ]
                .filter(Boolean)
                .join(' ')
                .trim()
            : 'Unassigned';

        const branchName =
          lead.branch?.name ||
          lead.branchName ||
          '';

        return {
          name: leadName,

          firstName:
            lead.firstName || '',

          lastName:
            lead.lastName || '',

          phone:
            lead.phone || '',

          email:
            lead.email || '',

          nationalId:
            lead.nationalId || '',

          source:
            lead.source || '',

          status:
            lead.status || '',

          assignedTo:
            assignedOfficer,

          branch:
            branchName,

          followUpDate:
            lead.followUpDate
              ? new Date(
                  lead.followUpDate
                ).toLocaleDateString()
              : '',

          createdAt:
            lead.createdAt
              ? new Date(
                  lead.createdAt
                ).toLocaleDateString()
              : '',

          notes:
            lead.notes || '',
        };
      });

      const headers = [
        {
          key: 'name',
          label: 'Name',
        },

        {
          key: 'firstName',
          label: 'First Name',
        },

        {
          key: 'lastName',
          label: 'Last Name',
        },

        {
          key: 'phone',
          label: 'Phone',
          forceString: true,
        },

        {
          key: 'email',
          label: 'Email',
        },

        {
          key: 'nationalId',
          label: 'National ID',
          forceString: true,
        },

        {
          key: 'source',
          label: 'Source',
        },

        {
          key: 'status',
          label: 'Status',
        },

        {
          key: 'assignedTo',
          label: 'Assigned To',
        },

        {
          key: 'branch',
          label: 'Branch',
        },

        {
          key: 'followUpDate',
          label: 'Follow-up Date',
        },

        {
          key: 'createdAt',
          label: 'Created Date',
        },

        {
          key: 'notes',
          label: 'Notes',
        },
      ];

      const response = await api.post(
        '/reports/exports/generate',
        {
          data: exportRows,

          headers,

          filename: `Leads_${new Date()
            .toISOString()
            .slice(0, 10)}`,

          format: 'xlsx',
        },
        {
          responseType: 'blob',
        }
      );

      const blob = new Blob(
        [response.data],
        {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        }
      );

      const url =
        window.URL.createObjectURL(blob);

      const anchor =
        document.createElement('a');

      anchor.href = url;

      anchor.download =
        `leads_export_${new Date()
          .toISOString()
          .slice(0, 10)}.xlsx`;

      document.body.appendChild(anchor);

      anchor.click();

      document.body.removeChild(anchor);

      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(
        'Lead export failed:',
        err
      );

      let message =
        'Lead export failed.';

      if (
        err.response?.data instanceof Blob
      ) {
        try {
          const text =
            await err.response.data.text();

          const parsed =
            JSON.parse(text);

          message =
            parsed.message ||
            message;
        } catch {
          // Keep default message.
        }
      } else {
        message =
          err.response?.data?.message ||
          err.message ||
          message;
      }

      alert(message);
    } finally {
      setIsExporting(false);
    }
  };

  // ═══════════════════════════════════════════════════════════════════
  // TABLE COLUMNS
  // ═══════════════════════════════════════════════════════════════════

  const columns = [
    {
      header: 'Name',

      render: (row) => (
        <Link
          to={`/leads/${row._id}`}
          style={{
            color: 'var(--primary)',
            fontWeight: 500,
          }}
        >
          {row.firstName}{' '}
          {row.lastName}
        </Link>
      ),
    },

    {
      header: 'Phone',

      accessor: 'phone',
    },

    {
      header: 'Source',

      render: (row) => (
        <span
          className="text-xs"
          style={{
            textTransform: 'capitalize',
          }}
        >
          {row.source
            ?.replace('-', ' ')}
        </span>
      ),
    },

    {
      header: 'Status',

      render: (row) => (
        <StatusBadge
          status={row.status}
          map={LEAD_STATUS_MAP}
        />
      ),
    },

    {
      header: 'Assigned To',

      render: (row) =>
        row.assignedTo ? (
          `${row.assignedTo.firstName || ''} ${
            row.assignedTo.lastName || ''
          }`.trim()
        ) : (
          <span
            style={{
              color: '#94a3b8',
            }}
          >
            Unassigned
          </span>
        ),
    },

    {
      header: 'Follow-up',

      render: (row) => {
        if (!row.followUpDate) {
          return (
            <span
              style={{
                color: '#cbd5e1',
              }}
            >
              —
            </span>
          );
        }

        const followUp =
          new Date(row.followUpDate);

        const today =
          new Date();

        const todayStart =
          new Date(
            today.getFullYear(),
            today.getMonth(),
            today.getDate()
          );

        const followUpStart =
          new Date(
            followUp.getFullYear(),
            followUp.getMonth(),
            followUp.getDate()
          );

        const isPast =
          followUpStart < todayStart;

        const isToday =
          followUpStart.getTime() ===
          todayStart.getTime();

        return (
          <span
            style={{
              color: isToday
                ? '#2563eb'
                : isPast
                ? '#dc2626'
                : '#374151',

              fontWeight:
                isToday || isPast
                  ? 600
                  : 400,
            }}
          >
            {isToday && '📌 '}

            {followUp.toLocaleDateString()}
          </span>
        );
      },
    },

    {
      header: 'Date',

      render: (row) =>
        row.createdAt
          ? new Date(
              row.createdAt
            ).toLocaleDateString()
          : '—',
    },

    {
      header: 'Actions',

      render: (row) => (
        <div
          className="flex gap-1"
          style={{
            flexWrap: 'wrap',
          }}
        >
          <Link
            to={`/leads/${row._id}`}
            className="btn btn-outline btn-sm"
          >
            👁️ View
          </Link>

          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={() =>
              openEditModal(row)
            }
          >
            ✏️ Edit
          </button>

          {perms.canReassign && (
            <button
              type="button"
              className="btn btn-sm"
              style={{
                background:
                  'var(--warning)',
                color: '#fff',
                border: 'none',
                cursor: 'pointer',
              }}
              onClick={() =>
                openReassignModal(row)
              }
            >
              👤 Reassign
            </button>
          )}

          {perms.canDelete && (
            <button
              type="button"
              className="btn btn-danger btn-sm"
              onClick={() =>
                handleDelete(row._id)
              }
            >
              🗑️
            </button>
          )}
        </div>
      ),
    },
  ];

  const filterableStatuses =
    LEAD_STATUSES.filter(
      (status) => status !== 'converted'
    );

  // ═══════════════════════════════════════════════════════════════════
  // ACTIVE FILTER TAGS
  // ═══════════════════════════════════════════════════════════════════

  const renderActiveTags = () => {
    if (!hasActiveFilters) {
      return null;
    }

    const tags = [];

    if (filters.search) {
      tags.push(
        <span
          key="search"
          style={F.tag}
        >
          Search: "{filters.search}"

          <button
            type="button"
            style={F.tagX}
            onClick={() =>
              clearFilter('search')
            }
          >
            ✕
          </button>
        </span>
      );
    }

    if (filters.status) {
      tags.push(
        <span
          key="status"
          style={F.tag}
        >
          Status: {filters.status}

          <button
            type="button"
            style={F.tagX}
            onClick={() =>
              clearFilter('status')
            }
          >
            ✕
          </button>
        </span>
      );
    }

    if (filters.source) {
      tags.push(
        <span
          key="source"
          style={F.tag}
        >
          Source: {filters.source}

          <button
            type="button"
            style={F.tagX}
            onClick={() =>
              clearFilter('source')
            }
          >
            ✕
          </button>
        </span>
      );
    }

    if (filters.assignedTo) {
      const officer =
        loanOfficers.find(
          (officer) =>
            officer._id ===
            filters.assignedTo
        );

      const officerName = officer
        ? [
            officer.firstName,
            officer.lastName,
          ]
            .filter(Boolean)
            .join(' ')
            .trim()
        : 'Selected officer';

      tags.push(
        <span
          key="assigned"
          style={F.tag}
        >
          Assigned: {officerName}

          <button
            type="button"
            style={F.tagX}
            onClick={() =>
              clearFilter(
                'assignedTo'
              )
            }
          >
            ✕
          </button>
        </span>
      );
    }

    if (
      filters.startDate &&
      filters.endDate
    ) {
      tags.push(
        <span
          key="date-range"
          style={F.tag}
        >
          Date: {filters.startDate} →
          {filters.endDate}

          <button
            type="button"
            style={F.tagX}
            onClick={() => {
              clearFilter('startDate');
              clearFilter('endDate');
            }}
          >
            ✕
          </button>
        </span>
      );
    } else if (filters.startDate) {
      tags.push(
        <span
          key="start-date"
          style={F.tag}
        >
          From: {filters.startDate}

          <button
            type="button"
            style={F.tagX}
            onClick={() =>
              clearFilter('startDate')
            }
          >
            ✕
          </button>
        </span>
      );
    } else if (filters.endDate) {
      tags.push(
        <span
          key="end-date"
          style={F.tag}
        >
          Until: {filters.endDate}

          <button
            type="button"
            style={F.tagX}
            onClick={() =>
              clearFilter('endDate')
            }
          >
            ✕
          </button>
        </span>
      );
    }

    return (
      <div style={F.activeRow}>
        <span
          style={{
            fontSize: 12,
            color: '#64748b',
            fontWeight: 600,
          }}
        >
          Active Filters (
          {activeFilterCount}):
        </span>

        {tags}

        {activeFilterCount > 1 && (
          <button
            type="button"
            style={F.clearAll}
            onClick={clearAllFilters}
          >
            Clear All
          </button>
        )}
      </div>
    );
  };

  // ═══════════════════════════════════════════════════════════════════
  // FILTER SECTION
  // ═══════════════════════════════════════════════════════════════════

  const renderFilters = () => (
    <div style={F.section}>
      <div style={F.row}>
        {/* Search */}
        <div style={F.group}>
          <label style={F.label}>
            Customer
          </label>

          <div style={F.searchWrap}>
            <span style={F.searchIcon}>
              🔍
            </span>

            <input
              style={F.input}
              placeholder="Search name, phone..."
              value={searchRaw}
              onChange={handleSearchChange}
            />
          </div>
        </div>

        {/* Status */}
        <div style={F.group}>
          <label style={F.label}>
            Status

            {filters.status && (
              <span style={F.badge}>
                1
              </span>
            )}
          </label>

          <select
            style={F.select}
            value={filters.status}
            onChange={(e) =>
              updateFilter(
                'status',
                e.target.value
              )
            }
          >
            <option value="">
              All Statuses
            </option>

            {filterableStatuses.map(
              (status) => (
                <option
                  key={status}
                  value={status}
                >
                  {status
                    .charAt(0)
                    .toUpperCase() +
                    status.slice(1)}
                </option>
              )
            )}
          </select>
        </div>

        {/* Source */}
        <div style={F.group}>
          <label style={F.label}>
            Source

            {filters.source && (
              <span style={F.badge}>
                1
              </span>
            )}
          </label>

          <select
            style={F.select}
            value={filters.source}
            onChange={(e) =>
              updateFilter(
                'source',
                e.target.value
              )
            }
          >
            <option value="">
              All Sources
            </option>

            {LEAD_SOURCES.map(
              (source) => (
                <option
                  key={source}
                  value={source}
                >
                  {source
                    .charAt(0)
                    .toUpperCase() +
                    source
                      .slice(1)
                      .replace(
                        '-',
                        ' '
                      )}
                </option>
              )
            )}
          </select>
        </div>

        {/* Assigned officer */}
        {!perms.isCO && (
          <div style={F.group}>
            <label style={F.label}>
              Assigned To

              {filters.assignedTo && (
                <span style={F.badge}>
                  1
                </span>
              )}
            </label>

            <select
              style={F.select}
              value={
                filters.assignedTo
              }
              onChange={(e) =>
                updateFilter(
                  'assignedTo',
                  e.target.value
                )
              }
            >
              <option value="">
                All Officers
              </option>

              {loanOfficers.map(
                (officer) => (
                  <option
                    key={officer._id}
                    value={officer._id}
                  >
                    {officer.firstName}{' '}
                    {officer.lastName}
                  </option>
                )
              )}
            </select>
          </div>
        )}

        {/* Start date */}
        <div style={F.group}>
          <label style={F.label}>
            Start Date

            {filters.startDate && (
              <span style={F.badge}>
                1
              </span>
            )}
          </label>

          <input
            type="date"
            style={F.date}
            value={filters.startDate}
            onChange={(e) =>
              updateFilter(
                'startDate',
                e.target.value
              )
            }
            max={
              filters.endDate ||
              undefined
            }
          />
        </div>

        {/* End date */}
        <div style={F.dateRow}>
          <input
            type="date"
            style={{
              ...F.date,
              borderRadius: '0',
            }}
            value={filters.endDate}
            onChange={(e) =>
              updateFilter(
                'endDate',
                e.target.value
              )
            }
            min={
              filters.startDate ||
              undefined
            }
          />

          <span style={F.dateSep}>
            →
          </span>
        </div>

        {/* Actions */}
        <div style={F.actions}>
          <button
            type="button"
            style={F.btnFilter}
            onClick={() =>
              fetchLeads()
            }
          >
            🔍 Filter
          </button>

          <button
            type="button"
            style={F.btnClear}
            onClick={
              clearAllFilters
            }
          >
            ✕ Clear
          </button>

          <button
            type="button"
            style={F.btnExport}
            onClick={handleExport}
            disabled={isExporting}
          >
            {isExporting
              ? '⏳ Exporting...'
              : '📥 Export Leads'}
          </button>

          {perms.canCreate && (
            <Link
              to="/leads/new"
              style={F.btnNew}
            >
              + New Lead
            </Link>
          )}
        </div>
      </div>

      {renderActiveTags()}
    </div>
  );

  // ═══════════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════════

  return (
    <div>
      {/* Collection officer notice */}
      {perms.isCO && (
        <div
          style={{
            background: '#fefce8',
            border: '1px solid #fde68a',
            borderRadius: 8,
            padding: '12px 16px',
            marginBottom: 12,
            fontSize: 13,
            color: '#92400e',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <span>🔒</span>

          <span>
            <strong>
              Collection Officer View:
            </strong>{' '}
            You can view and edit lead
            details, but reassignment and
            deletion are restricted.
          </span>
        </div>
      )}

      {/* Statistics */}
      {stats && (
        <div className="grid grid-4 gap-1 mb-2">
          <div
            className="card"
            style={{
              textAlign: 'center',
              padding: '16px',
            }}
          >
            <p
              className="text-muted text-xs"
              style={{
                marginBottom: 4,
              }}
            >
              Total Leads
            </p>

            <p
              style={{
                fontSize: 24,
                fontWeight: 700,
              }}
            >
              {stats.total}
            </p>
          </div>

          <div
            className="card"
            style={{
              textAlign: 'center',
              padding: '16px',
            }}
          >
            <p
              className="text-muted text-xs"
              style={{
                marginBottom: 4,
              }}
            >
              Converted
            </p>

            <p
              style={{
                fontSize: 24,
                fontWeight: 700,
                color:
                  'var(--success)',
              }}
            >
              {stats.converted}
            </p>
          </div>

          <div
            className="card"
            style={{
              textAlign: 'center',
              padding: '16px',
            }}
          >
            <p
              className="text-muted text-xs"
              style={{
                marginBottom: 4,
              }}
            >
              Conversion Rate
            </p>

            <p
              style={{
                fontSize: 24,
                fontWeight: 700,
              }}
            >
              {stats.conversionRate}%
            </p>
          </div>

          <div
            className="card"
            style={{
              textAlign: 'center',
              padding: '16px',
            }}
          >
            <p
              className="text-muted text-xs"
              style={{
                marginBottom: 4,
              }}
            >
              Follow-ups Due
            </p>

            <p
              style={{
                fontSize: 24,
                fontWeight: 700,
                color:
                  stats.followUpDue > 0
                    ? 'var(--warning)'
                    : 'inherit',
              }}
            >
              {stats.followUpDue}
            </p>
          </div>
        </div>
      )}

      {/* Filters */}
      {renderFilters()}

      {/* Converted notice */}
      {stats?.converted > 0 && (
        <div
          style={{
            background: '#f0fdf4',
            border:
              '1px solid #bbf7d0',
            borderRadius: 8,
            padding: '10px 16px',
            marginBottom: 12,
            fontSize: 13,
            color: '#166534',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <span>✅</span>

          <span>
            <strong>
              {stats.converted} converted
              lead
              {stats.converted !== 1
                ? 's'
                : ''}
            </strong>{' '}
            are hidden from this list.
            View in Loans section.
          </span>
        </div>
      )}

      {/* Lead table */}
      <div className="card">
        <DataTable
          columns={columns}
          data={visibleLeads}
          pagination={pagination}
          onPageChange={(nextPage) =>
            setPage(nextPage)
          }
        />
      </div>

      {/* ═══════════════════════════════════════════════════════════════
          EDIT LEAD MODAL
      ═══════════════════════════════════════════════════════════════ */}

      <Modal
        isOpen={showEditModal}
        onClose={() =>
          setShowEditModal(false)
        }
        title="Edit Lead"
        size="600px"
      >
        {editingLead && (
          <form
            onSubmit={handleEditSubmit}
          >
            {editError && (
              <div
                style={{
                  color: 'red',
                  marginBottom: 12,
                  padding: '8px 12px',
                  background: '#fef2f2',
                  borderRadius: 6,
                  fontSize: 14,
                }}
              >
                {editError}
              </div>
            )}

            <div className="grid grid-2 gap-1">
              <div className="form-group">
                <label>
                  First Name *
                </label>

                <input
                  className="form-control"
                  required
                  maxLength={50}
                  value={
                    editForm.firstName
                  }
                  onChange={(e) =>
                    setEditForm({
                      ...editForm,
                      firstName:
                        e.target.value,
                    })
                  }
                />
              </div>

              <div className="form-group">
                <label>
                  Last Name *
                </label>

                <input
                  className="form-control"
                  required
                  maxLength={50}
                  value={
                    editForm.lastName
                  }
                  onChange={(e) =>
                    setEditForm({
                      ...editForm,
                      lastName:
                        e.target.value,
                    })
                  }
                />
              </div>
            </div>

            <div className="grid grid-2 gap-1">
              <div className="form-group">
                <label>
                  Phone *
                </label>

                <input
                  className="form-control"
                  required
                  value={
                    editForm.phone
                  }
                  onChange={(e) =>
                    setEditForm({
                      ...editForm,
                      phone:
                        e.target.value,
                    })
                  }
                />
              </div>

              <div className="form-group">
                <label>
                  Email
                </label>

                <input
                  className="form-control"
                  type="email"
                  value={
                    editForm.email
                  }
                  onChange={(e) =>
                    setEditForm({
                      ...editForm,
                      email:
                        e.target.value,
                    })
                  }
                />
              </div>
            </div>

            <div className="grid grid-2 gap-1">
              <div className="form-group">
                <label>
                  National ID
                </label>

                <input
                  className="form-control"
                  value={
                    editForm.nationalId
                  }
                  onChange={(e) =>
                    setEditForm({
                      ...editForm,
                      nationalId:
                        e.target.value,
                    })
                  }
                  placeholder="Leave blank to keep existing"
                />
              </div>

              <div className="form-group">
                <label>
                  Source
                </label>

                <select
                  className="form-control"
                  value={
                    editForm.source
                  }
                  onChange={(e) =>
                    setEditForm({
                      ...editForm,
                      source:
                        e.target.value,
                    })
                  }
                >
                  {LEAD_SOURCES.map(
                    (source) => (
                      <option
                        key={source}
                        value={source}
                      >
                        {source
                          .charAt(0)
                          .toUpperCase() +
                          source
                            .slice(1)
                            .replace(
                              '-',
                              ' '
                            )}
                      </option>
                    )
                  )}
                </select>
              </div>
            </div>

            <div className="form-group">
              <label>
                Address
              </label>

              <input
                className="form-control"
                value={
                  editForm.address
                }
                onChange={(e) =>
                  setEditForm({
                    ...editForm,
                    address:
                      e.target.value,
                  })
                }
              />
            </div>

            <div className="form-group">
              <label>
                Follow-up Date
              </label>

              <input
                className="form-control"
                type="date"
                value={
                  editForm.followUpDate
                }
                onChange={(e) =>
                  setEditForm({
                    ...editForm,
                    followUpDate:
                      e.target.value,
                  })
                }
              />
            </div>

            <div className="form-group">
              <label>
                Notes
              </label>

              <textarea
                className="form-control"
                rows="3"
                maxLength={2000}
                value={
                  editForm.notes
                }
                onChange={(e) =>
                  setEditForm({
                    ...editForm,
                    notes:
                      e.target.value,
                  })
                }
              />
            </div>

            <div className="flex gap-1 mt-2">
              <button
                type="submit"
                className="btn btn-primary"
                disabled={editSaving}
              >
                {editSaving
                  ? 'Saving...'
                  : 'Update Lead'}
              </button>

              <button
                type="button"
                className="btn btn-secondary"
                onClick={() =>
                  setShowEditModal(
                    false
                  )
                }
              >
                Cancel
              </button>
            </div>
          </form>
        )}
      </Modal>

      {/* ═══════════════════════════════════════════════════════════════
          REASSIGN MODAL
      ═══════════════════════════════════════════════════════════════ */}

      {perms.canReassign && (
        <Modal
          isOpen={showReassignModal}
          onClose={
            closeReassignModal
          }
          title="Reassign Lead to Loan Officer"
          size="480px"
        >
          {reassigningLead && (
            <form
              onSubmit={
                handleReassignSubmit
              }
            >
              <div
                style={{
                  background: '#f8f9fa',
                  borderRadius: 8,
                  padding: '12px 16px',
                  marginBottom: 16,
                  border:
                    '1px solid #e5e7eb',
                }}
              >
                <p
                  style={{
                    margin: 0,
                    fontWeight: 600,
                    fontSize: 15,
                  }}
                >
                  {
                    reassigningLead.firstName
                  }{' '}
                  {
                    reassigningLead.lastName
                  }
                </p>

                <p
                  style={{
                    margin:
                      '4px 0 0',
                    color: '#6b7280',
                    fontSize: 13,
                  }}
                >
                  📞{' '}
                  {
                    reassigningLead.phone
                  }

                  {reassigningLead.email &&
                    ` • ✉️ ${reassigningLead.email}`}
                </p>

                {reassigningLead.assignedTo && (
                  <p
                    style={{
                      margin:
                        '6px 0 0',
                      fontSize: 13,
                    }}
                  >
                    <span
                      style={{
                        color:
                          '#6b7280',
                      }}
                    >
                      Currently:
                    </span>{' '}
                    <strong>
                      {
                        reassigningLead
                          .assignedTo
                          .firstName
                      }{' '}
                      {
                        reassigningLead
                          .assignedTo
                          .lastName
                      }
                    </strong>
                  </p>
                )}
              </div>

              {reassignError && (
                <div
                  style={{
                    color: 'red',
                    marginBottom: 12,
                    padding:
                      '8px 12px',
                    background:
                      '#fef2f2',
                    borderRadius: 6,
                    fontSize: 14,
                    border:
                      '1px solid #fecaca',
                  }}
                >
                  {reassignError}
                </div>
              )}

              <div className="form-group">
                <label
                  style={{
                    fontWeight: 600,
                    marginBottom: 6,
                    display: 'block',
                  }}
                >
                  Select Loan Officer{' '}
                  <span
                    style={{
                      color: 'red',
                    }}
                  >
                    *
                  </span>
                </label>

                {loLoading ? (
                  <div
                    style={{
                      padding:
                        '12px 0',
                      color:
                        '#6b7280',
                      fontSize: 14,
                    }}
                  >
                    Loading...
                  </div>
                ) : loanOfficers.length ===
                  0 ? (
                  <div
                    style={{
                      padding: 12,
                      background:
                        '#fefce8',
                      borderRadius: 6,
                      fontSize: 14,
                      color:
                        '#92400e',
                      border:
                        '1px solid #fde68a',
                    }}
                  >
                    ⚠️ No Loan
                    Officers found.
                  </div>
                ) : (
                  <select
                    className="form-control"
                    value={selectedLO}
                    onChange={(e) =>
                      setSelectedLO(
                        e.target.value
                      )
                    }
                    required
                    style={{
                      fontSize: 14,
                      padding:
                        '10px 12px',
                    }}
                  >
                    <option value="">
                      -- Select --
                    </option>

                    {[
                      ...loanOfficers,
                    ]
                      .sort(
                        (a, b) =>
                          `${a.firstName || ''} ${a.lastName || ''}`.localeCompare(
                            `${b.firstName || ''} ${b.lastName || ''}`
                          )
                      )
                      .map(
                        (officer) => (
                          <option
                            key={
                              officer._id
                            }
                            value={
                              officer._id
                            }
                          >
                            {
                              officer.firstName
                            }{' '}
                            {
                              officer.lastName
                            }

                            {officer.phone
                              ? ` — ${officer.phone}`
                              : ''}

                            {officer.branch
                              ?.name
                              ? ` (${officer.branch.name})`
                              : ''}
                          </option>
                        )
                      )}
                  </select>
                )}
              </div>

              <div
                style={{
                  marginTop: 12,
                  padding:
                    '10px 12px',
                  background:
                    '#eff6ff',
                  borderRadius: 6,
                  fontSize: 13,
                  color: '#1e40af',
                  border:
                    '1px solid #bfdbfe',
                }}
              >
                ℹ️ The lead will be
                immediately reassigned.
              </div>

              <div
                className="flex gap-1 mt-3"
                style={{
                  justifyContent:
                    'flex-end',
                }}
              >
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={
                    closeReassignModal
                  }
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={
                    reassignSaving ||
                    !selectedLO ||
                    loLoading ||
                    loanOfficers.length ===
                      0
                  }
                  style={{
                    background:
                      'var(--warning)',
                    borderColor:
                      'var(--warning)',
                    color: '#fff',
                  }}
                >
                  {reassignSaving ? (
                    <span>
                      <span
                        className="spinner"
                        style={{
                          width: 14,
                          height: 14,
                          borderWidth: 2,
                          marginRight: 6,
                          display:
                            'inline-block',
                          verticalAlign:
                            'middle',
                        }}
                      />

                      Reassigning...
                    </span>
                  ) : (
                    '👤 Reassign Lead'
                  )}
                </button>
              </div>
            </form>
          )}
        </Modal>
      )}
    </div>
  );
}
